import type { Button } from '@property.com.au/ui-kit';
import { mediaHelper } from '@property.com.au/ui-kit';
import type { ComponentProps, FunctionComponent } from 'react';
import { useEffect } from 'react';
import type { WebLinkProps } from '../../../app-kit/components/WebLink/WebLink';
import { WebLink } from '../../../app-kit/components/WebLink/WebLink';
import { updatePropertyRelationshipModalPageSource } from '../../../components/PropertyRelationship/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModalPageSource';
import { followPropertyModalPageSource } from '../../../components/PropertyRelationship/FollowPropertyModal/FollowPropertyModalPageSource';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';
import { replace } from '@rea-argonaut/link';
import { usePropertyId } from '../../../contexts/PropertyIdContext/PropertyIdContext';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { useTrackPropertyRelationshipModal } from '../PropertyRelationship/useTrackPropertyRelationshipModal';
import { useFollowedProperties } from '../../hooks/useFollowedProperties';
import { useAccountQuery } from '../../../hooks/useAccountQuery';
import styled from 'styled-components';
import { useWaitForHydrationWorkaround } from '../PropertyRelationship/useWaitForHydrationWorkaround';
import { BeforeYouBuyCta } from './subcomponents/BeforeYouBuyCta/BeforeYouBuyCta';
import {
  openSetRelationshipModalWithMyHomePreselectedUrlHash,
  ThisIsMyHomeCta,
} from './subcomponents/ThisIsMyHomeCta/ThisIsMyHomeCta';
import { isNotNullOrUndefinedValue } from '../../../helpers/isNotNullOrUndefinedValue';

const StyledWebLink = styled(WebLink)`
  width: 100%;

  ${mediaHelper('tabletMin')} {
    width: auto;
  }
`;

export interface CtaButtonProps extends ComponentProps<typeof Button> {
  ctaVariant: 'BeforeYouBuy' | 'BuildingAndPest' | 'ThisIsMyHome';
  text: string;
}

interface HeaderCtasProps {
  headerCtas: (WebLinkProps | CtaButtonProps)[];
}

const isWebLinkProps = (cta: WebLinkProps | CtaButtonProps): cta is WebLinkProps => 'url' in cta;

export const HeaderCtas: FunctionComponent<HeaderCtasProps> = ({ headerCtas }) => {
  const { followedProperties } = useFollowedProperties();
  const accountQueryResult = useAccountQuery();
  const signedIn = isNotNullOrUndefinedValue(accountQueryResult.data?.account);
  const url = useUrlContext();
  const propertyId = usePropertyId();
  const { displayModal } = usePropertyPageModalContext();
  const isHydrated = useWaitForHydrationWorkaround();
  const trackPropertyRelationshipModal = useTrackPropertyRelationshipModal();

  const propertyRelationship = followedProperties?.find(
    ({ id }) => id === propertyId,
  )?.relationship;
  const isFollowed = propertyRelationship !== undefined;

  useEffect(() => {
    if (
      isHydrated &&
      signedIn &&
      url.hash === openSetRelationshipModalWithMyHomePreselectedUrlHash
    ) {
      if (isFollowed) {
        displayModal({ name: 'updatePropertyRelationshipModalMyHomeAsDefault' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: updatePropertyRelationshipModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      } else {
        displayModal({ name: 'followPropertyModalWithMyHomeAsDefault' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: followPropertyModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      }
      replace(location.pathname + location.search);
    }
  }, [signedIn, isHydrated, url.hash, displayModal, trackPropertyRelationshipModal, isFollowed]);

  return headerCtas.map((cta) => {
    if (isWebLinkProps(cta))
      return <StyledWebLink {...cta} key={cta.title} clientSideRouting={false} />;

    if (cta.ctaVariant === 'BeforeYouBuy') return <BeforeYouBuyCta cta={cta} key={cta.text} />;

    if (cta.ctaVariant === 'ThisIsMyHome') return <ThisIsMyHomeCta cta={cta} key={cta.text} />;

    return null;
  });
};
